/* Profiles: how fresh every table's profile is, what its baseline came to, and what its columns look
 * like — across the estate, then one table at a time.
 *
 * **Reads only what profiling already stored.** The catalogue listing, the profile facts, and each
 * profiled table's sketches and drift. Nothing on this screen touches a customer's source; the only
 * control that does is Run profile, and it says what it costs.
 *
 * **Absence is never drawn as a low figure.** A table nobody profiled is "Never profiled" with a dashed
 * card, not a baseline of zero; a column nobody could measure is hatched, not an empty bar; a baseline
 * that could not be computed is listed by name rather than placed at the bottom of the scale.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { useConnections } from '@/entities/connection';
import { can, PROFILE_DATASETS_PERMISSION, useIdentity } from '@/entities/session';
import { useDatasetFacts, useDatasets } from '@/features/catalog-browser';
import {
  driftVerdict,
  requestProfile,
  share,
  useEstateProfiles,
} from '@/features/profiler';
import type { DriftVerdict } from '@/features/profiler';
import type {
  BaselineFinding,
  DatasetFacts,
  DatasetSketches,
  SketchDrift,
} from '@/shared/api/generated/catalog';
import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { BrandLoader } from '@/shared/ui/BrandLoader';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import type { DatasetObjectType } from '@/shared/ui/DatasetObjectTypeIcon';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useCountUp } from '@/shared/ui/motion';

import {
  FINDING_KINDS,
  FRESHNESS_ORDER,
  baselineTone,
  completenessTone,
  donutSegments,
  freshnessOf,
} from '../model/estate';
import type { Freshness, Tone } from '../model/estate';
import { EstateMap } from './EstateCharts';
import { scrollToStart } from './scroll';
import { DONUT_VAR, FILL_VAR, FRESHNESS_TONE, VERDICT_TONE } from './tones';

/** How many profiles the bulk run asks for at once. Each is up to twenty grouped scans of a
 * customer's table, so the run is paced rather than fired all at once. */
const BULK_BATCH = 5;
/** How often the profile facts are re-read while a requested profile has not landed yet. */
const REQUESTED_POLL_MS = 10_000;

const VERDICTS: readonly DriftVerdict[] = ['stable', 'moved', 'replaced', 'unmeasurable'];
const BANDS: readonly Tone[] = ['good', 'watch', 'risk', 'none'];

type Filter = 'all' | Freshness;

interface Row {
  readonly id: string;
  readonly name: string;
  readonly objectName: string;
  readonly objectType: DatasetObjectType;
  readonly connectionId: string;
  readonly freshness: Freshness;
  readonly facts: DatasetFacts | undefined;
  readonly sketches: DatasetSketches | null | undefined;
  readonly drift: SketchDrift | null | undefined;
}

export function ProfilesScreen(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const datasets = useDatasets({ search: '', connectionId: null });
  const connections = useConnections();
  const identity = useIdentity();
  const listed = useMemo(() => datasets.data?.items ?? [], [datasets.data]);
  const ids = useMemo(() => listed.map((dataset) => dataset.id), [listed]);
  const facts = useDatasetFacts(ids);
  const [now] = useState(() => new Date());
  const [filter, setFilter] = useState<Filter>('all');
  const [requested, setRequested] = useState<ReadonlySet<string>>(new Set());
  const [flash, setFlash] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);

  const factsMap = facts.data;
  const profiledIds = useMemo(
    () =>
      listed
        .filter(
          (dataset) => (factsMap?.get(dataset.id)?.last_profiled_at ?? null) !== null,
        )
        .map((dataset) => dataset.id),
    [listed, factsMap],
  );
  const estate = useEstateProfiles(profiledIds);

  /* While a requested profile has not landed, re-read the facts now and then, so its card turns from
     "Profiling" into a profile without a reload. */
  const waiting = [...requested].some(
    (id) => (factsMap?.get(id)?.last_profiled_at ?? null) === null,
  );
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'dataset-facts'] });
    }, REQUESTED_POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [waiting, queryClient]);

  const canRun = can(identity.data, PROFILE_DATASETS_PERMISSION);
  const runTitle = canRun
    ? t('profiles.run.title')
    : t('profiles.run.needs', { permission: PROFILE_DATASETS_PERMISSION });

  const loading =
    datasets.isPending || connections.isPending || (ids.length > 0 && facts.isPending);
  const failure = datasets.error ?? facts.error;

  /* Open at the top, for the reason `ProfileDetail` gives: the shell keeps its scroll across
     navigation, so coming back from a table would otherwise land wherever that page was. The head
     exists only once the estate has loaded, so this waits for it. */
  useEffect(() => {
    if (!loading) scrollToStart(topRef.current);
  }, [loading]);

  const connectionName = (id: string): string =>
    connections.data?.items.find((connection) => connection.id === id)?.name ??
    t('profiles.unlistedSource');

  const rows: Row[] = listed.map((dataset) => {
    const own = factsMap?.get(dataset.id);
    return {
      id: dataset.id,
      name: `${dataset.schema_name}.${dataset.object_name}`,
      objectName: dataset.object_name,
      objectType: dataset.object_type,
      connectionId: dataset.connection_id,
      freshness: freshnessOf(own, now),
      facts: own,
      sketches: estate.sketches.get(dataset.id),
      drift: estate.drift.get(dataset.id),
    };
  });

  const counts: Record<Freshness, number> = { stale: 0, ageing: 0, fresh: 0, never: 0 };
  for (const row of rows) counts[row.freshness] += 1;
  const neverIds = rows.filter((row) => row.freshness === 'never').map((row) => row.id);

  async function runProfiles(targets: readonly string[]): Promise<number> {
    let failed = 0;
    for (let start = 0; start < targets.length; start += BULK_BATCH) {
      const batch = targets.slice(start, start + BULK_BATCH);
      setRequested((current) => new Set([...current, ...batch]));
      const results = await Promise.allSettled(batch.map((id) => requestProfile(id)));
      const refused = batch.filter((_, index) => results[index]?.status === 'rejected');
      failed += refused.length;
      if (refused.length > 0) {
        setRequested(
          (current) => new Set([...current].filter((id) => !refused.includes(id))),
        );
      }
    }
    return failed;
  }

  function runAll(): void {
    if (!canRun || neverIds.length === 0) return;
    setFlash(t('profiles.runAll.started', { count: neverIds.length }));
    void runProfiles(neverIds).then((failed) => {
      if (failed > 0) setFlash(t('profiles.runAll.failed', { count: failed }));
    });
  }

  function runOne(row: Row): void {
    if (!canRun || requested.has(row.id)) return;
    setFlash(t('profiles.run.started', { name: row.name }));
    void runProfiles([row.id]).then((failed) => {
      if (failed > 0) setFlash(t('profiles.runAll.failed', { count: failed }));
    });
  }

  if (loading) {
    return (
      <section className="lq-pf">
        <span
          className="lq-skeleton__bar lq-pf__skeleton"
          role="status"
          aria-label={t('profiles.title')}
        />
      </section>
    );
  }

  if (failure !== null) {
    return (
      <section className="lq-pf">
        <Alert tone="error" title={t('profiles.loadFailed')}>
          {failure.message}
        </Alert>
      </section>
    );
  }

  const shown = rows
    .filter((row) => filter === 'all' || row.freshness === filter)
    .sort(
      (a, b) =>
        FRESHNESS_ORDER.indexOf(a.freshness) - FRESHNESS_ORDER.indexOf(b.freshness) ||
        (a.facts?.profile_baseline ?? 101) - (b.facts?.profile_baseline ?? 101),
    );

  return (
    <section className="lq-pf">
      <div className="lq-pf__head" ref={topRef}>
        <div className="lq-pf__titles">
          <h1 className="lq-pf__title">{t('profiles.title')}</h1>
          <p className="lq-pf__lede">{t('profiles.lede')}</p>
        </div>
        <button
          type="button"
          className="lq-pf__runAll"
          disabled={!canRun || neverIds.length === 0}
          title={runTitle}
          onClick={runAll}
        >
          {t('profiles.runAll', { count: neverIds.length })}
        </button>
      </div>

      {flash !== null && (
        <p className="lq-pf__flash" role="status">
          {flash}
        </p>
      )}
      {estate.failed > 0 && (
        <p className="lq-pf__note">
          {t('profiles.readsFailed', { count: estate.failed })}
        </p>
      )}
      {datasets.data?.truncated === true && (
        <p className="lq-pf__note">{t('profiles.truncated', { count: rows.length })}</p>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title={t('profiles.estateEmpty.title')}
          guidance={t('profiles.estateEmpty.guidance')}
        />
      ) : (
        <>
          <div className="lq-pf__estate">
            <FreshnessCard
              counts={counts}
              total={rows.length}
              filter={filter}
              onFilter={setFilter}
            />
            <SignalsCard rows={rows} />
          </div>

          <div className="lq-pf__chartRow">
            <EstateMap
              tables={rows.map((row) => ({
                id: row.id,
                name: row.name,
                objectName: row.objectName,
                baseline: row.facts?.profile_baseline ?? null,
                profiledAt: row.facts?.last_profiled_at ?? null,
                rows: row.facts?.rows_at_last_profile ?? null,
              }))}
              total={rows.length}
              now={now}
            />
          </div>

          <div
            className="lq-pf__pills"
            role="group"
            aria-label={t('profiles.filter.label')}
          >
            {(['all', ...FRESHNESS_ORDER] as const).map((key) => (
              <button
                key={key}
                type="button"
                className="lq-pf__pill"
                aria-pressed={filter === key}
                onClick={() => {
                  setFilter(key);
                }}
              >
                {key === 'all'
                  ? t('profiles.filter.all')
                  : t(`profiles.freshness.${key}`)}
                <span className="lq-pf__pillCount">
                  {key === 'all' ? rows.length : counts[key]}
                </span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <p className="lq-pf__empty">{t('profiles.empty')}</p>
          ) : (
            <div className="lq-pf__cards">
              {shown.map((row) => (
                <TableCard
                  key={row.id}
                  row={row}
                  now={now}
                  connection={connectionName(row.connectionId)}
                  requested={requested.has(row.id)}
                  canRun={canRun}
                  runTitle={runTitle}
                  onRun={() => {
                    runOne(row);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ the estate cards */

function FreshnessCard({
  counts,
  total,
  filter,
  onFilter,
}: {
  readonly counts: Readonly<Record<Freshness, number>>;
  readonly total: number;
  readonly filter: Filter;
  readonly onFilter: (next: Filter) => void;
}): ReactElement {
  const { t } = useTranslation();
  const profiled = total - counts.never;
  const counted = useCountUp(profiled, (n) => Math.round(n).toLocaleString());
  const stops = donutSegments(counts)
    .map(
      (segment) =>
        `${DONUT_VAR[segment.freshness]} ${segment.from.toFixed(2)}% ${segment.to.toFixed(2)}%, var(--surface-raised) ${segment.to.toFixed(2)}% ${(segment.to + 0.6).toFixed(2)}%`,
    )
    .join(', ');

  return (
    <article className="lq-pf__panel">
      <h2 className="lq-pf__panelTitle">{t('profiles.freshness.title')}</h2>
      <div className="lq-pf__donutRow">
        <div
          className="lq-pf__donut"
          style={{ background: `conic-gradient(${stops})` }}
          aria-hidden="true"
        >
          <div className="lq-pf__donutHole">
            <div className="lq-pf__donutFigure">
              <span ref={counted}>{profiled.toLocaleString()}</span>
              <span className="lq-pf__donutTotal">/{total}</span>
            </div>
            <div className="lq-pf__donutLabel">{t('profiles.freshness.profiled')}</div>
          </div>
        </div>
        <div className="lq-pf__legend">
          {FRESHNESS_ORDER.map((key) => (
            <button
              key={key}
              type="button"
              className="lq-pf__legendRow"
              data-tone={FRESHNESS_TONE[key]}
              aria-pressed={filter === key}
              title={t(`profiles.freshness.hint.${key}`)}
              onClick={() => {
                onFilter(filter === key ? 'all' : key);
              }}
            >
              <span className="lq-pf__swatch" data-freshness={key} />
              <span className="lq-pf__legendLabel">{t(`profiles.freshness.${key}`)}</span>
              <strong>{counts[key]}</strong>
            </button>
          ))}
        </div>
      </div>
    </article>
  );
}

function SignalsCard({ rows }: { readonly rows: readonly Row[] }): ReactElement {
  const { t } = useTranslation();
  const bands: Record<Tone, number> = { good: 0, watch: 0, risk: 0, none: 0 };
  const verdicts: Record<DriftVerdict, number> = {
    stable: 0,
    moved: 0,
    replaced: 0,
    unmeasurable: 0,
  };
  const findings: Record<BaselineFinding['kind'], number> = {
    all_null: 0,
    mostly_null: 0,
    sparse: 0,
    constant: 0,
    dominated: 0,
  };
  let columns = 0;
  let compared = 0;
  for (const row of rows) {
    for (const sketch of row.sketches?.columns ?? []) {
      columns += 1;
      bands[completenessTone(sketch.completeness)] += 1;
    }
    for (const finding of row.sketches?.baseline.findings ?? [])
      findings[finding.kind] += 1;
    for (const drift of row.drift?.columns ?? []) {
      compared += 1;
      verdicts[driftVerdict(drift)] += 1;
    }
  }
  const columnTotal = Math.max(columns, 1);
  const compareTotal = Math.max(compared, 1);

  return (
    <article className="lq-pf__panel">
      <div className="lq-pf__panelHead">
        <h2 className="lq-pf__panelTitle lq-pf__grow">{t('profiles.signals.title')}</h2>
        <span className="lq-pf__big">{columns.toLocaleString()}</span>
      </div>
      <div className="lq-pf__signal">
        <div className="lq-pf__eyebrow">{t('profiles.signals.completeness')}</div>
        <div className="lq-pf__stack" data-grow="">
          {BANDS.map((band) => (
            <div
              key={band}
              className="lq-pf__stackPart"
              data-fill={band}
              title={t('profiles.signals.columns', { count: bands[band] })}
              style={{ width: `${((bands[band] / columnTotal) * 100).toFixed(2)}%` }}
            />
          ))}
        </div>
        <div className="lq-pf__keys">
          {BANDS.map((band) => (
            <span key={band} className="lq-pf__key">
              <span className="lq-pf__keyDot" data-fill={band} />
              {t(`profiles.band.${band}`)}
              <strong>{bands[band]}</strong>
            </span>
          ))}
        </div>
      </div>
      <div className="lq-pf__signal">
        <div className="lq-pf__eyebrow">{t('profiles.signals.since')}</div>
        <div className="lq-pf__stack" data-grow="">
          {VERDICTS.map((verdict) => (
            <div
              key={verdict}
              className="lq-pf__stackPart"
              data-fill={VERDICT_TONE[verdict]}
              title={t('profiles.signals.verdictCount', {
                count: verdicts[verdict],
                verdict: t(`profiles.verdict.${verdict}`).toLowerCase(),
              })}
              style={{
                width: `${((verdicts[verdict] / compareTotal) * 100).toFixed(2)}%`,
              }}
            />
          ))}
        </div>
        <div className="lq-pf__keys">
          {VERDICTS.map((verdict) => (
            <span key={verdict} className="lq-pf__key">
              <span className="lq-pf__keyDot" data-fill={VERDICT_TONE[verdict]} />
              {t(`profiles.verdict.${verdict}`)}
              <strong>{verdicts[verdict]}</strong>
            </span>
          ))}
        </div>
      </div>
      <div className="lq-pf__findings">
        {FINDING_KINDS.map((kind) => (
          <span key={kind} className="lq-pf__finding" data-found={findings[kind] > 0}>
            <strong>{findings[kind]}</strong> {t(`profiles.finding.${kind}`)}
          </span>
        ))}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ one table's card */

function TableCard({
  row,
  now,
  connection,
  requested,
  canRun,
  runTitle,
  onRun,
}: {
  readonly row: Row;
  readonly now: Date;
  readonly connection: string;
  readonly requested: boolean;
  readonly canRun: boolean;
  readonly runTitle: string;
  readonly onRun: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const never = row.freshness === 'never';
  const baseline = row.facts?.profile_baseline ?? null;
  const tone = baselineTone(baseline);
  const verdicts = new Map(
    (row.drift?.columns ?? []).map((drift) => [drift.column, driftVerdict(drift)]),
  );
  const findings = row.sketches?.baseline.findings.length ?? 0;
  const changed = [...verdicts.values()].filter((verdict) => verdict !== 'stable').length;
  const age =
    row.facts?.last_profiled_at === undefined || row.facts.last_profiled_at === null
      ? null
      : (ageText(t, row.facts.last_profiled_at, now) ?? row.facts.last_profiled_at);
  const rowsAt = row.facts?.rows_at_last_profile ?? null;

  const body = (
    <>
      <div className="lq-pf__cardHead">
        <div className="lq-pf__cardTitles">
          <div className="lq-pf__cardName">
            <DatasetObjectTypeIcon objectType={row.objectType} />
            {row.name}
          </div>
          <div className="lq-pf__cardConn">{connection}</div>
        </div>
        <div
          className="lq-pf__ring"
          title={t('profiles.card.ring')}
          data-tone={tone}
          style={
            baseline === null
              ? undefined
              : {
                  background: `conic-gradient(${FILL_VAR[tone]} 0 ${String(baseline)}%, var(--pf-track) ${String(baseline)}% 100%)`,
                }
          }
        >
          <span className="lq-pf__ringHole">{baseline ?? '—'}</span>
        </div>
      </div>

      {!never && row.sketches !== undefined && row.sketches !== null && (
        <>
          <div className="lq-pf__skyline" aria-hidden="true">
            {row.sketches.columns.map((sketch) => {
              const verdict = verdicts.get(sketch.column);
              const bar =
                sketch.completeness === null
                  ? t('profiles.card.bar.unmeasured', { column: sketch.column })
                  : t('profiles.card.bar.measured', {
                      column: sketch.column,
                      share: share(sketch.completeness),
                    });
              return (
                <div
                  key={sketch.column}
                  className="lq-pf__sky"
                  title={
                    verdict === undefined
                      ? bar
                      : t('profiles.card.bar.verdict', {
                          bar,
                          verdict: t(`profiles.verdict.${verdict}`).toLowerCase(),
                        })
                  }
                >
                  <span
                    className="lq-pf__skyDot"
                    data-fill={
                      verdict === undefined || verdict === 'stable'
                        ? undefined
                        : VERDICT_TONE[verdict]
                    }
                  />
                  <div
                    className="lq-pf__skyBar"
                    data-fill={completenessTone(sketch.completeness)}
                    style={{
                      height:
                        sketch.completeness === null
                          ? '100%'
                          : `${Math.max(6, sketch.completeness * 100).toFixed(1)}%`,
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div className="lq-pf__cardFacts">
            <span>
              {t('profiles.card.columns', { count: row.sketches.columns.length })}
            </span>
            <span>·</span>
            <span>
              {rowsAt === 0
                ? t('profiles.card.empty')
                : t('profiles.card.rows', { rows: compactNumber(rowsAt ?? 0) })}
            </span>
            <span>·</span>
            <span className="lq-pf__cardFindings" data-found={findings > 0}>
              {t('profiles.card.findings', { count: findings })}
            </span>
            {changed > 0 && (
              <span className="lq-pf__cardChanged">
                {t('profiles.card.changed', { count: changed })}
              </span>
            )}
          </div>
        </>
      )}

      {!never && row.sketches === undefined && (
        <span className="lq-skeleton__bar lq-pf__skyLoading" aria-hidden="true" />
      )}

      {!never && row.sketches === null && (
        <div className="lq-pf__unmeasured">{t('profiles.card.noCurrent')}</div>
      )}

      {never && !requested && (
        <div className="lq-pf__unmeasured">{t('profiles.card.notMeasured')}</div>
      )}

      {never && requested && (
        <div className="lq-pf__profiling" role="status">
          <BrandLoader size="md" />
          <span>
            <strong>{t('profiles.card.profiling')}</strong>
            {t('profiles.card.profilingTail')}
          </span>
        </div>
      )}

      <div className="lq-pf__cardFoot">
        <span className="lq-pf__fresh" data-tone={FRESHNESS_TONE[row.freshness]}>
          <span className="lq-pf__freshDot" />
          {never || age === null
            ? t('profiles.freshness.never')
            : t('profiles.card.freshLabel', {
                label: t(`profiles.freshness.${row.freshness}`),
                age,
              })}
        </span>
        <span className="lq-pf__grow" />
        {never ? (
          <button
            type="button"
            className="lq-pf__run"
            disabled={!canRun || requested}
            title={runTitle}
            onClick={onRun}
          >
            {requested ? t('profiles.card.requested') : t('profiles.card.run')}
          </button>
        ) : (
          <span className="lq-pf__open">{t('profiles.card.open')}</span>
        )}
      </div>
    </>
  );

  /* A profiled table's whole card opens it; a never-profiled one is not a link, because it has
     nothing to open yet and holds its own Run profile button. */
  return never ? (
    <article className="lq-pf__card lq-pf__card--never">{body}</article>
  ) : (
    <Link
      className="lq-pf__card"
      to={generatePath(SCREEN_PATHS.profile, { datasetId: row.id })}
    >
      {body}
    </Link>
  );
}
